import React, { useState, useEffect, useMemo } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Search, FileText, Edit, Trash2, Plus, Save, Mail, Download, Copy, Eye, Calendar, User, Link, CheckCircle, Lock, Unlock } from 'lucide-react';
import { useApi, useApiMutation } from '../hooks/useApi';
import { quotesApi, customersApi, serviceItemsApi, companiesApi } from '../services/api';
import { useToast } from '../hooks/use-toast';
import QuoteTracking from './QuoteTracking';
import QuoteAcceptanceDetails from './QuoteAcceptanceDetails';

const QuoteManager = React.forwardRef(({ companyId, onLoadQuote, onNewQuote, currentQuote, onQuoteSaved, currentSavedQuoteId, savedQuoteId, onOpenCustomer }, ref) => {
  const [isQuoteManagerOpen, setIsQuoteManagerOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isSaving, setIsSaving] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [selectedAcceptanceQuote, setSelectedAcceptanceQuote] = useState(null);
  const [pendingLoadQuote, setPendingLoadQuote] = useState(null);
  const [brands, setBrands] = useState([]);
  const [brandFilter, setBrandFilter] = useState('all');

  useEffect(() => {
    companiesApi.getAll().then(r => setBrands(r.data || [])).catch(() => {});
  }, []);
  const [isAcceptanceDetailsOpen, setIsAcceptanceDetailsOpen] = useState(false);

  const { toast } = useToast();
  
  // Angebote BRAND-ÜBERGREIFEND laden — Brand ist Spalte + Filter, kein
  // verstecktes Kriterium mehr (gleiche Logik wie Kunden/Einnahmen/Fixkosten)
  const { data: quotes, loading, refetch, error } = useApi(
    () => quotesApi.getAll('', statusFilter),
    [statusFilter]
  );

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' })
  const [quotesLocal, setQuotesLocal] = useState([]);
  
  useEffect(() => {
    // Sort quotes by creation date (newest first) - this is the basic sort
    const brandName = (id) => brands.find(c => c.id === id)?.name || '—';
    const sortedQuotes = Array.isArray(quotes) ?
      [...quotes]
        .map(q => ({ ...q, _brand: brandName(q.companyId) }))
        .sort((a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at)) :
      [];
    setQuotesLocal(sortedQuotes);
  }, [quotes, brands]);
  
  // Sort quotes based on config
  const sortedQuotes = useMemo(() => {
    if (!quotesLocal.length) return []
    
    const sorted = [...quotesLocal].sort((a, b) => {
      let aVal = a[sortConfig.key]
      let bVal = b[sortConfig.key]
      
      // Handle different data types
      if (sortConfig.key === 'createdAt' || sortConfig.key === 'updatedAt') {
        aVal = new Date(aVal || 0)
        bVal = new Date(bVal || 0)
      } else if (sortConfig.key === 'total' || sortConfig.key === 'subtotal') {
        aVal = parseFloat((sortConfig.key === 'total' && a.acceptedTotals ? a.acceptedTotals.total : aVal) || 0)
        bVal = parseFloat((sortConfig.key === 'total' && b.acceptedTotals ? b.acceptedTotals.total : bVal) || 0)
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = (bVal || '').toLowerCase()
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
    
    return sorted
  }, [quotesLocal, sortConfig])

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }
  const { mutate: createQuote } = useApiMutation();

  // Reload quotes list when dialog opens
  useEffect(() => {
    if (isQuoteManagerOpen && companyId) {
      refetch();
    }
  }, [isQuoteManagerOpen, companyId]);

  const seedExamples = async () => {
    if (!companyId) return;
    try {
      const existing = quotes || [];
      if (existing.length > 0) {
        toast({ title: 'Hinweis', description: 'Es existieren bereits Angebote – Seed übersprungen.' });
        return;
      }

      // Load some services for price composition
      const servicesResp = await serviceItemsApi.getAll(companyId);
      const allServices = servicesResp?.data || [];
      const pick = (n) => allServices.slice(0, n).map(s => ({ serviceId: s.id, isOptional: false, isIncluded: true, quantity: 1 }));

      const basePayload = {
        companyId,
        subpageCount: 3,
        discount: { oneTime: { type: 'percentage', value: 0 }, monthly: { type: 'percentage', value: 0 } },
        selectedPackages: [],
        notes: 'Seed-Beispielangebot',
        includeDetailedDescriptions: false
      };

      const examples = [
        {
          customerData: { company: 'Musterkunde GmbH', name: 'Max Mustermann', address: 'Kundenstraße 1', city: '10115 Berlin', phone: '+49 30 123456', email: 'max@musterkunde.de' },
          selectedServices: pick(3)
        },
        {
          customerData: { company: 'Acme AG', name: 'Anna Beispiel', address: 'Beispielweg 5', city: '20095 Hamburg', phone: '+49 40 987654', email: 'anna@acme-ag.de' },
          selectedServices: pick(5)
        },
        {
          customerData: { company: 'Kreativstudio UG', name: 'Lukas Design', address: 'Ateliergasse 12', city: '50667 Köln', phone: '+49 221 112233', email: 'lukas@kreativstudio.de' },
          selectedServices: pick(2)
        }
      ];

      for (const ex of examples) {
        await createQuote(() => quotesApi.create({ ...basePayload, ...ex }));
      }

      toast({ title: 'Beispiel-Angebote erstellt', description: '3 Angebote wurden angelegt.' });
      refetch();
    } catch (e) {
      toast({ title: 'Seed fehlgeschlagen', description: String(e), variant: 'destructive' });
    }
  };

  const { mutate: updateQuote } = useApiMutation();
  const { mutate: deleteQuote } = useApiMutation();
  const { mutate: duplicateQuote } = useApiMutation();
  const { mutate: generatePdf } = useApiMutation();
  const { mutate: sendEmail } = useApiMutation();

  const handleSaveCurrentQuote = async () => {
    if (!companyId) {
      toast({ title: 'Firma wählen', description: 'Bitte wählen Sie zuerst Ihre Firma (Absender) aus.', variant: 'destructive' });
      return;
    }
    if (!currentQuote || !currentQuote.customerInfo?.company) {
      toast({ title: 'Kundendaten unvollständig', description: 'Bitte geben Sie mindestens den Firmennamen des Kunden ein.', variant: 'destructive' });
      return;
    }

    setIsSaving(true);
    try {
      // 1) Kunde anlegen/aktualisieren (A): per E-Mail suchen, sonst Firma+Name
      // Dedupe-Fix: Suchstring "Firma Name" matchte kein einzelnes Feld ->
      // bei leerer E-Mail wurde bei JEDEM Save ein Duplikat angelegt.
      // Jetzt: nach E-Mail ODER Firmenname suchen, Treffer exakt abgleichen.
      let existingCustomer = null;
      const customerInfo = currentQuote.customerInfo || {};
      const norm = (v) => (v || '').trim().toLowerCase();
      try {
        const q = (customerInfo.email || customerInfo.company || '').trim();
        if (q) {
          const res = await customersApi.search(q, companyId, 10);
          const found = Array.isArray(res?.data) ? res.data : [];
          existingCustomer =
            (customerInfo.email && found.find(c => norm(c.email) === norm(customerInfo.email))) ||
            found.find(c => norm(c.company) === norm(customerInfo.company) && norm(c.name) === norm(customerInfo.name)) ||
            found.find(c => norm(c.company) === norm(customerInfo.company)) ||
            null;
        }
      } catch (_) {}

      let customerId = existingCustomer?.id;
      if (!customerId) {
        try {
          const createRes = await customersApi.create({
            company: customerInfo.company || '',
            name: customerInfo.name || '',
            address: customerInfo.address || '',
            city: customerInfo.city || '',
            phone: customerInfo.phone || '',
            email: customerInfo.email || '',
            companyId: companyId,
          });
          customerId = createRes?.data?.id;
        } catch (e) {
          // Wenn Kunde schon existiert (z.B. wegen gleicher E-Mail), ignorieren wir und fahren fort
        }
      }

      // 2) Services mappen mit allen neuen Feldern - berechne auch die Totals
      // name/description/price/type IMMER mitschreiben — Custom-Positionen haben
      // keinen Katalog-Eintrag, aus dem sie rekonstruierbar wären (Vorfall 19.07.)
      const selectedServices = Object.entries(currentQuote.selectedServices).map(([key, service]) => ({
        serviceId: service.id,
        name: service.name || null,
        description: service.description || null,
        price: service.price ?? service.originalPrice ?? 0,
        priceMax: service.priceMax ?? null,
        type: service.type || 'einmalig',
        customPrice: service.price !== service.originalPrice ? service.price : null,
        customDescription: service.notes || null,
        isOptional: !!service.isOptional,
        isIncluded: !service.isOptional,
        quantity: service.quantity || 1,
        customDiscount: service.customDiscount || null,
        // Calculate totals for backend
        basePrice: service.originalPrice || service.price || 0,
        finalPrice: service.price || service.originalPrice || 0,
        // Alternative Positionen
        alternatives: service.alternatives || [],
        selectedAlternativeId: service.selectedAlternativeId || null
      }));

      // 3) Quote Payload
      const quoteData = {
        companyId,
        customerId: customerId || null,
        customerData: customerInfo,
        subpageCount: currentQuote.subpageCount,
        selectedServices,
        selectedPackages: currentQuote.selectedPackages || [],
        discount: currentQuote.discount,
        // notes/includeDetailedDescriptions NICHT hart mitschicken: '' bzw. false
        // haben beim Speichern die vorhandenen Notizen (z.B. Telefonprotokolle)
        // und die Einstellung ueberschrieben.
        ...(currentQuote.notes !== undefined ? { notes: currentQuote.notes } : {}),
        ...(currentQuote.includeDetailedDescriptions !== undefined
          ? { includeDetailedDescriptions: currentQuote.includeDetailedDescriptions }
          : {}),
      };

      // 4) Check if we should update existing quote
      let saved;
      
      // Use savedQuoteId if available and no explicit currentSavedQuoteId 
      const quoteIdToUpdate = currentSavedQuoteId || savedQuoteId
      
      if (quoteIdToUpdate) {
        // Update existing quote
        console.log('🔍 Updating existing quote:', quoteIdToUpdate)
        saved = await updateQuote(() => quotesApi.update(quoteIdToUpdate, quoteData));
        toast({
          title: 'Angebot aktualisiert ✅',
          description: `Das Angebot ${saved?.quoteNumber || ''} wurde erfolgreich aktualisiert.`,
        });
      } else {
        // Create new quote
        console.log('🔍 Creating new quote with data:', quoteData)
        
        try {
          const response = await quotesApi.create(quoteData);
          saved = response.data || response; // Handle different response formats
          console.log('🔍 Direct API response:', saved)
        } catch (error) {
          console.error('🔍 API Error:', error)
          saved = await createQuote(() => quotesApi.create(quoteData));
        }
        
        console.log('🔍 Final saved object:', saved)
        toast({
          title: 'Angebot gespeichert ✅',
          description: `Das Angebot ${saved?.quoteNumber || saved?.quote_number || ''} wurde erfolgreich erstellt.`,
        });
      }
      
      // Callback to parent with saved quote ID
      if (onQuoteSaved && saved?.id) {
        console.log('🔍 Setting savedQuoteId:', saved.id)
        onQuoteSaved(saved.id);
      } else {
        console.log('🔍 No saved.id or onQuoteSaved callback', {saved, onQuoteSaved})
        // Fallback: Try to set any ID we can find
        const possibleId = saved?.id || saved?.data?.id || saved?._id
        if (possibleId && onQuoteSaved) {
          console.log('🔍 Using fallback ID:', possibleId)
          onQuoteSaved(possibleId)
        }
      }
      
      // Liste aktualisieren (sofort + Refetch)
      if (saved) setQuotesLocal(prev => [{...saved}, ...(prev || [])]);
      refetch();
      setStatusFilter((prev) => prev === 'all' ? 'Entwurf' : 'all');
      setTimeout(() => setStatusFilter('all'), 0);

      setIsQuoteManagerOpen(true);
    } catch (error) {
      const msg = (error && error.message) ? error.message : 'Angebot konnte nicht gespeichert werden.';
      toast({ title: 'Fehler', description: msg, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const doLoadQuote = (quote) => {
    onLoadQuote(quote);
    setIsQuoteManagerOpen(false);
    setPendingLoadQuote(null);
  };

  const handleLoadQuote = (quote) => {
    // Nur warnen, wenn im Arbeitsbereich WIRKLICH ungespeicherte Arbeit liegt:
    // Inhalte vorhanden UND noch kein gespeichertes Angebot. (Vorher feuerte
    // die Warnung bei fast jedem Klick, weil currentQuote immer ein Objekt ist.)
    const ws = currentQuote || {};
    const wsHasContent =
      !!(ws.customerInfo?.company || ws.customerInfo?.name) ||
      Object.keys(ws.selectedServices || {}).length > 0;
    const hasRealUnsavedChanges = wsHasContent && !currentSavedQuoteId;

    if (hasRealUnsavedChanges) {
      setPendingLoadQuote(quote); // eleganter Dialog statt window.confirm
      return;
    }
    doLoadQuote(quote);
  };

  const handleDeleteQuote = async (quoteId) => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses Angebot löschen möchten?')) {
      try {
        await deleteQuote(() => quotesApi.delete(quoteId));
        
        toast({
          title: "Angebot gelöscht",
          description: "Das Angebot wurde erfolgreich gelöscht.",
        });
        
        refetch();
      } catch (error) {
        toast({
          title: "Fehler",
          description: "Angebot konnte nicht gelöscht werden.",
          variant: "destructive",
        });
      }
    }
  };

  const handleDuplicateQuote = async (quoteId) => {
    try {
      const duplicated = await duplicateQuote(() => quotesApi.duplicate(quoteId));
      
      toast({
        title: "Angebot dupliziert",
        description: "Das Angebot wurde erfolgreich dupliziert.",
      });
      
      refetch();
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Angebot konnte nicht dupliziert werden.",
        variant: "destructive",
      });
    }
  };

  const handleGeneratePdf = async (quoteId) => {
    try {
      // Get the quote to find viewHash
      const quote = quotes.find(q => q.id === quoteId);
      
      if (quote?.viewHash) {
        // Open public view in new window for printing (same layout as Step 4)
        const printUrl = `${window.location.origin}/quote/view/${quote.viewHash}?print=true`;
        const printWindow = window.open(printUrl, '_blank');
        
        toast({
          title: "PDF Druckvorschau",
          description: "Die Druckansicht wird in einem neuen Tab geöffnet. Wählen Sie dort 'Als PDF speichern'.",
        });
      } else {
        // Fallback: Generate viewHash first
        toast({
          title: "Link wird generiert...",
          description: "Bitte warten Sie einen Moment.",
        });
        
        // Generate link first
        await quotesApi.generateLink(quoteId);
        
        // Reload quotes to get updated viewHash (Liste kommt aus useApi -> refetch)
        const refreshedQuotes = await quotesApi.getAll(companyId);
        refetch();

        const updatedQuote = (Array.isArray(refreshedQuotes) ? refreshedQuotes : []).find(q => q.id === quoteId);
        if (updatedQuote?.viewHash) {
          const printUrl = `${window.location.origin}/quote/view/${updatedQuote.viewHash}?print=true`;
          window.open(printUrl, '_blank');
          
          toast({
            title: "PDF Druckvorschau",
            description: "Die Druckansicht wird in einem neuen Tab geöffnet. Wählen Sie dort 'Als PDF speichern'.",
          });
        }
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "PDF-Vorschau konnte nicht geöffnet werden.",
        variant: "destructive",
      });
    }
  };

  const handleGenerateAcceptanceAudit = async (quoteId) => {
    try {
      // Generate acceptance audit PDF on backend
      await generatePdf(() => quotesApi.generateAcceptanceAudit(quoteId));
      
      // Download MIT Auth-Header (Blob) — ein <a href> schickt den JWT nicht
      // mit und lief dadurch in 403
      const res = await quotesApi.fetchAcceptanceAudit(quoteId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Annahme-Audit-${quoteId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Annahme-Audit PDF generiert ✅",
        description: "Das rechtssichere Annahme-Audit wurde erfolgreich erstellt und wird heruntergeladen.",
      });
    } catch (error) {
      toast({
        title: "Fehler",
        description: error?.response?.data?.detail || "Annahme-Audit PDF konnte nicht erstellt werden.",
        variant: "destructive",
      });
    }
  };

  const handleSendEmail = async (quoteId) => {
    try {
      await sendEmail(() => quotesApi.sendEmail(quoteId));
      
      toast({
        title: "E-Mail versendet",
        description: "Das Angebot wurde erfolgreich per E-Mail versendet.",
      });
      
      refetch();
    } catch (error) {
      toast({
        title: "Fehler",
        description: "E-Mail konnte nicht versendet werden.",
        variant: "destructive",
      });
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [quoteId]: true }));
    
    try {
      await updateQuote(() => quotesApi.update(quoteId, { status: newStatus }));
      
      toast({
        title: "Status aktualisiert",
        description: `Status wurde erfolgreich auf "${newStatus}" geändert.`,
      });
      
      refetch();
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Status konnte nicht geändert werden.",
        variant: "destructive",
      });
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  const handleEditQuote = (quote) => {
    setEditingQuote({
      ...quote,
      customerData: {
        company: quote.customerData?.company || '',
        name: quote.customerData?.name || '',
        address: quote.customerData?.address || '',
        city: quote.customerData?.city || '',
        phone: quote.customerData?.phone || '',
        email: quote.customerData?.email || ''
      }
    });
    setIsEditModalOpen(true);
  };

  // Zugriff auf den oeffentlichen Link widerrufen bzw. wieder freigeben.
  // Der Link selbst bleibt bestehen — er liefert nur nichts mehr aus.
  const handleToggleLink = async (quote) => {
    const disable = !quote.linkDisabled;
    if (disable && !window.confirm(
      `Zugriff auf ${quote.quoteNumber || 'dieses Angebot'} sperren?\n\n` +
      'Wer den Link hat, sieht das Angebot ab sofort nicht mehr. ' +
      'Du kannst den Zugriff jederzeit wieder freigeben — der Link bleibt derselbe.'
    )) return;
    try {
      await updateQuote(() => quotesApi.update(quote.id, { linkDisabled: disable }));
      toast({
        title: disable ? 'Zugriff gesperrt 🔒' : 'Zugriff wieder frei 🔓',
        description: disable
          ? 'Der öffentliche Link liefert das Angebot nicht mehr aus.'
          : 'Das Angebot ist über den bekannten Link wieder erreichbar.',
      });
      refetch();
    } catch (error) {
      toast({ title: 'Fehler', description: 'Status konnte nicht geändert werden.', variant: 'destructive' });
    }
  };

  const handleViewAcceptance = (quote) => {
    setSelectedAcceptanceQuote(quote);
    setIsAcceptanceDetailsOpen(true);
  };

  const handleSaveEditedQuote = async () => {
    if (!editingQuote) return;

    try {
      await updateQuote(() => quotesApi.update(editingQuote.id, {
        customerData: editingQuote.customerData,
        notes: editingQuote.notes
      }));
      
      toast({
        title: "Angebot gespeichert",
        description: "Die Änderungen wurden erfolgreich gespeichert.",
      });
      
      setIsEditModalOpen(false);
      setEditingQuote(null);
      refetch();
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Änderungen konnten nicht gespeichert werden.",
        variant: "destructive",
      });
    }
  };
  
  // Expose handleSaveCurrentQuote to parent via ref
  React.useImperativeHandle(ref, () => ({
    handleSaveCurrentQuote: handleSaveCurrentQuote
  }));

  const statusOptions = [
    { value: 'Entwurf', label: 'Entwurf', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'Versendet', label: 'Versendet', color: 'bg-blue-100 text-blue-800' },
    { value: 'Angenommen', label: 'Angenommen', color: 'bg-green-100 text-green-800' },
    { value: 'Abgelehnt', label: 'Abgelehnt', color: 'bg-red-100 text-red-800' }
  ];

  const filteredQuotes = quotes?.filter(quote => {
    const matchesSearch = 
      quote.quoteNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.customerData?.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.customerData?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  }) || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Entwurf': return 'bg-yellow-100 text-yellow-800';
      case 'Versendet': return 'bg-blue-100 text-blue-800';
      case 'Angenommen': return 'bg-green-100 text-green-800';
      case 'Abgelehnt': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-4">
            {/* Ungespeicherte-Änderungen-Dialog (statt window.confirm) */}
            <Dialog open={!!pendingLoadQuote} onOpenChange={(v) => !v && setPendingLoadQuote(null)}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-slate-900">Nicht gespeicherte Änderungen</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-slate-600">
                  Ihr aktueller Entwurf wurde noch nicht gespeichert. Wenn Sie{' '}
                  <span className="font-medium text-slate-800">{pendingLoadQuote?.quoteNumber}</span>{' '}
                  laden, geht der Entwurf verloren.
                </p>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setPendingLoadQuote(null)} className="border-slate-300">
                    Abbrechen
                  </Button>
                  <Button
                    onClick={() => doLoadQuote(pendingLoadQuote)}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    Trotzdem laden
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="flex justify-between items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  placeholder="Angebot oder Kunde suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger className="w-52">
                  <SelectValue placeholder="Brand filtern" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Brands</SelectItem>
                  {brands.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Status filtern" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="Entwurf">Entwurf</SelectItem>
                  <SelectItem value="Versendet">Versendet</SelectItem>
                  <SelectItem value="Angenommen">Angenommen</SelectItem>
                  <SelectItem value="Abgelehnt">Abgelehnt</SelectItem>
                  <SelectItem value="Verhandlung">Verhandlung</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-8">Lade Angebote...</div>
            ) : (!quotesLocal || quotesLocal.length === 0) ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-14 h-14 mx-auto mb-4 text-gray-200" />
                <p className="text-lg font-semibold text-slate-700 mb-1">Noch keine Angebote</p>
                <p className="text-sm text-slate-500 mb-5">Erstellen Sie Ihr erstes professionelles Angebot.</p>
                {onNewQuote && (
                  <button
                    onClick={onNewQuote}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Neues Angebot erstellen
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50 w-[110px]" 
                        onClick={() => handleSort('quoteNumber')}
                      >
                        Angebots-Nr. {sortConfig.key === 'quoteNumber' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50 w-[180px]"
                        onClick={() => handleSort('customerData.company')}
                      >
                        Kunde {sortConfig.key === 'customerData.company' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-slate-50 w-[110px]"
                        onClick={() => handleSort('_brand')}
                      >
                        Brand {sortConfig.key === '_brand' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50 w-[100px]"
                        onClick={() => handleSort('createdAt')}
                      >
                        Erstellt {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50 w-[100px]"
                        onClick={() => handleSort('updatedAt')}
                      >
                        Aktualisiert {sortConfig.key === 'updatedAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50 w-[130px]"
                        onClick={() => handleSort('total')}
                      >
                        Gesamtbetrag {sortConfig.key === 'total' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50 w-[120px]"
                        onClick={() => handleSort('status')}
                      >
                        Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead className="w-[100px]">Annahme</TableHead>
                      <TableHead className="w-[100px]">Tracking</TableHead>
                      <TableHead className="text-right w-[190px] min-w-[190px] sticky right-0 bg-slate-50 z-20 border-l border-slate-200">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(sortedQuotes.filter(quote => {
                      if (brandFilter !== 'all' && quote.companyId !== brandFilter) return false;
                      const match = (
                        quote.quoteNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        quote.customerData?.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        quote.customerData?.name?.toLowerCase().includes(searchTerm.toLowerCase())
                      );
                      return match;
                    })).map((quote) => (
                      <TableRow key={quote.id}>
                        <TableCell className="font-medium">{quote.quoteNumber}</TableCell>
                        <TableCell>
                          {/* Klick auf den Kunden öffnet seine Kartei */}
                          {quote.customerId && onOpenCustomer ? (
                            <button
                              type="button"
                              onClick={() => onOpenCustomer(quote.customerId)}
                              title="Kundenkartei öffnen"
                              className="text-left rounded-md px-2 py-1 -mx-2 hover:bg-amber-50 border border-transparent hover:border-amber-300 transition-colors"
                            >
                              <p className="font-medium text-slate-800 hover:text-amber-700">{quote.customerData?.company || 'Unbekannt'}</p>
                              <p className="text-sm text-gray-500">{quote.customerData?.name || ''}</p>
                            </button>
                          ) : (
                            <div>
                              <p className="font-medium">{quote.customerData?.company || 'Unbekannt'}</p>
                              <p className="text-sm text-gray-500">{quote.customerData?.name || ''}</p>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-slate-100 text-slate-700 border-slate-300 text-xs">{quote._brand}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{new Date(quote.createdAt).toLocaleDateString('de-DE')}</div>
                          <div className="text-xs text-gray-500">{new Date(quote.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{new Date(quote.updatedAt).toLocaleDateString('de-DE')}</div>
                          <div className="text-xs text-gray-500">{new Date(quote.updatedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</div>
                        </TableCell>
                        <TableCell className="font-semibold">
                          {(() => {
                            // Angenommen: nur was der Kunde beauftragt hat (ohne abgewählte Optionen)
                            const amt = quote.acceptedTotals || quote
                            const fmt = (v) => (v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            const differs = quote.acceptedTotals && (
                              amt.total !== quote.total || amt.monthlyTotal !== (quote.monthlyTotal || 0) || amt.yearlyTotal !== (quote.yearlyTotal || 0))
                            return (
                              <>
                                {fmt(amt.total)}€
                                {amt.monthlyTotal > 0 && (
                                  <div className="text-sm text-orange-600">+{fmt(amt.monthlyTotal)}€/Monat</div>
                                )}
                                {amt.yearlyTotal > 0 && (
                                  <div className="text-sm text-purple-600">+{fmt(amt.yearlyTotal)}€/Jahr</div>
                                )}
                                {differs && (
                                  <div className="text-xs font-normal text-slate-500" title="Maximalbetrag inkl. aller optionalen Positionen">
                                    Angebot: {fmt(quote.total)}€
                                    {quote.monthlyTotal > 0 && ` + ${fmt(quote.monthlyTotal)}€/Mon.`}
                                    {quote.yearlyTotal > 0 && ` + ${fmt(quote.yearlyTotal)}€/Jahr`}
                                  </div>
                                )}
                              </>
                            )
                          })()}
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={quote.status} 
                            onValueChange={(newStatus) => handleStatusChange(quote.id, newStatus)}
                            disabled={updatingStatus[quote.id]}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue>
                                <div className="flex items-center gap-2">
                                  <Badge className={getStatusColor(quote.status)} variant="outline">
                                    {quote.status}
                                  </Badge>
                                </div>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {statusOptions.map((status) => (
                                <SelectItem key={status.value} value={status.value}>
                                  <div className="flex items-center gap-2">
                                    <Badge className={status.color} variant="outline">
                                      {status.label}
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {updatingStatus[quote.id] && (
                            <div className="text-xs text-gray-500 mt-1">Speichert...</div>
                          )}
                        </TableCell>

                        <TableCell>
                          {quote.acceptance || quote.acceptedAt ? (
                            <div className="flex flex-col gap-1">
                              <Badge className="bg-green-100 text-green-800 border-green-300 text-xs">
                                ✅ Angenommen
                              </Badge>
                              <Button
                                size="sm"
                                variant="link"
                                onClick={() => handleViewAcceptance(quote)}
                                className="text-xs text-blue-600 hover:text-blue-800 p-0 h-auto"
                              >
                                Details anzeigen
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Offen</span>
                          )}
                        </TableCell>

                        <TableCell>
                          {/* Klick öffnet den Statistik-Dialog (Aufrufe + Sitzungen) */}
                          <QuoteTracking
                            quoteId={quote.id}
                            quotesData={{ views: quote.viewCount || 0, lastViewed: quote.lastViewed }}
                            trigger={
                              <button
                                type="button"
                                title="Statistiken anzeigen (Aufrufe, Sitzungen, Aktionen)"
                                className="flex flex-col gap-1 items-start rounded-md px-2 py-1 -mx-2 hover:bg-amber-50 border border-transparent hover:border-amber-300 transition-colors text-left"
                              >
                                {quote.viewCount > 0 ? (
                                  <>
                                    <div className="flex items-center gap-1 text-sm">
                                      <Eye className="w-3 h-3 text-blue-600" />
                                      <span className="font-medium">{quote.viewCount}</span>
                                      <span className="text-xs text-gray-500">Aufrufe</span>
                                    </div>
                                    {quote.lastViewed && (
                                      <div className="text-xs text-gray-500">
                                        {new Date(quote.lastViewed).toLocaleDateString('de-DE')}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-xs text-gray-400">Nicht angesehen</span>
                                )}
                                <span className="text-[10px] text-amber-600 font-medium">Details →</span>
                              </button>
                            }
                          />
                        </TableCell>

                        {/* Steuerelemente bleiben beim Querscrollen sichtbar —
                            die Tabelle ist breiter als das Fenster. */}
                        <TableCell className="text-right min-w-[190px] sticky right-0 bg-white z-10 border-l border-slate-200">
                          <div className="flex flex-col gap-1.5 items-end">
                            {/* Zeile 1: Hauptaktionen */}
                            <div className="flex flex-wrap justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleLoadQuote(quote)}
                                title="In Kalkulator laden"
                                className="h-7 w-7 p-0"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>

                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditQuote(quote)}
                                title="Angebot bearbeiten"
                                className="text-blue-600 hover:text-blue-800 h-7 w-7 p-0"
                              >
                                <User className="w-3 h-3" />
                              </Button>
                              
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    let viewUrl = quote.viewUrl;
                                    if (!viewUrl) {
                                      const response = await quotesApi.generateViewUrl(quote.id);
                                      viewUrl = response.data.viewUrl;
                                      refetch();
                                    }
                                    navigator.clipboard.writeText(viewUrl);
                                    toast({
                                      title: "Ansichts-Link kopiert! 🔗",
                                      description: "Der öffentliche Ansichts-Link wurde in die Zwischenablage kopiert.",
                                    });
                                  } catch (error) {
                                    toast({
                                      title: "Fehler",
                                      description: "Link konnte nicht kopiert werden.",
                                      variant: "destructive",
                                    });
                                  }
                                }}
                                title="Öffentlichen Ansichts-Link kopieren"
                                className="text-purple-600 hover:text-purple-800 h-7 w-7 p-0"
                              >
                                <Link className="w-3 h-3" />
                              </Button>
                              
                              {(quote.viewUrl || quote.viewHash) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const url = quote.viewUrl || `${window.location.origin}/quote/view/${quote.viewHash}`;
                                    window.open(url, '_blank');
                                  }}
                                  title="Angebot in neuem Tab öffnen"
                                  className="text-green-600 hover:text-green-800 h-7 w-7 p-0"
                                >
                                  <Eye className="w-3 h-3" />
                                </Button>
                              )}

                              {(quote.viewUrl || quote.viewHash) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleToggleLink(quote)}
                                  title={quote.linkDisabled
                                    ? 'Zugriff wieder freigeben'
                                    : 'Zugriff sperren — Link funktioniert nicht mehr'}
                                  className={`h-7 w-7 p-0 ${quote.linkDisabled
                                    ? 'text-amber-600 hover:text-amber-800'
                                    : 'text-slate-500 hover:text-slate-800'}`}
                                >
                                  {quote.linkDisabled ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                                </Button>
                              )}
                            </div>
                            
                            {/* Zeile 2: Weitere Aktionen */}
                            <div className="flex flex-wrap justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleGeneratePdf(quote.id)}
                                title="PDF generieren"
                                className="h-7 w-7 p-0"
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                              
                              {(quote.acceptance || quote.acceptedAt) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleGenerateAcceptanceAudit(quote.id)}
                                  title="Rechtssicheres Annahme-Audit PDF exportieren"
                                  className="text-green-700 hover:text-green-900 border-green-300 hover:border-green-400 h-7 w-7 p-0"
                                >
                                  <CheckCircle className="w-3 h-3" />
                                </Button>
                              )}
                              
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDuplicateQuote(quote.id)}
                                title="Angebot duplizieren"
                                className="h-7 w-7 p-0"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                              
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteQuote(quote.id)}
                                className="text-red-600 hover:text-red-800 h-7 w-7 p-0"
                                title="Angebot löschen"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

      {/* Edit Quote Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Angebot bearbeiten - {editingQuote?.quoteNumber}
            </DialogTitle>
          </DialogHeader>
          
          {editingQuote && (
            <div className="space-y-6">
              {/* Angebotsinformationen */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Angebotsinformationen
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-gray-600">Angebots-Nr.</Label>
                    <p className="font-medium">{editingQuote.quoteNumber}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Erstellt am</Label>
                    <p className="font-medium">{new Date(editingQuote.createdAt).toLocaleDateString('de-DE')}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Gesamtbetrag</Label>
                    <p className="font-medium text-blue-600">{editingQuote.total?.toLocaleString('de-DE')}€</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Status</Label>
                    <Select 
                      value={editingQuote.status} 
                      onValueChange={(value) => setEditingQuote({...editingQuote, status: value})}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>

              {/* Kundendaten */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Kundendaten
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-company">Firma *</Label>
                    <Input
                      id="edit-company"
                      value={editingQuote.customerData?.company || ''}
                      onChange={(e) => setEditingQuote({
                        ...editingQuote,
                        customerData: {
                          ...editingQuote.customerData,
                          company: e.target.value
                        }
                      })}
                      placeholder="Firmenname"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-name">Ansprechpartner</Label>
                    <Input
                      id="edit-name"
                      value={editingQuote.customerData?.name || ''}
                      onChange={(e) => setEditingQuote({
                        ...editingQuote,
                        customerData: {
                          ...editingQuote.customerData,
                          name: e.target.value
                        }
                      })}
                      placeholder="Name des Ansprechpartners"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-email">E-Mail</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editingQuote.customerData?.email || ''}
                      onChange={(e) => setEditingQuote({
                        ...editingQuote,
                        customerData: {
                          ...editingQuote.customerData,
                          email: e.target.value
                        }
                      })}
                      placeholder="email@firma.de"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-phone">Telefon</Label>
                    <Input
                      id="edit-phone"
                      value={editingQuote.customerData?.phone || ''}
                      onChange={(e) => setEditingQuote({
                        ...editingQuote,
                        customerData: {
                          ...editingQuote.customerData,
                          phone: e.target.value
                        }
                      })}
                      placeholder="+49 123 456789"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="edit-address">Adresse</Label>
                    <Input
                      id="edit-address"
                      value={editingQuote.customerData?.address || ''}
                      onChange={(e) => setEditingQuote({
                        ...editingQuote,
                        customerData: {
                          ...editingQuote.customerData,
                          address: e.target.value
                        }
                      })}
                      placeholder="Straße, Hausnummer, PLZ Stadt"
                    />
                  </div>
                </div>
              </Card>

              {/* Notizen */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3">Interne Notizen</h3>
                <Textarea
                  value={editingQuote.notes || ''}
                  onChange={(e) => setEditingQuote({
                    ...editingQuote,
                    notes: e.target.value
                  })}
                  placeholder="Interne Notizen zum Angebot..."
                  rows={3}
                />
              </Card>

              {/* Aktionen */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingQuote(null);
                  }}
                >
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleSaveEditedQuote}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Acceptance Details Dialog */}
      {selectedAcceptanceQuote && (
        <QuoteAcceptanceDetails
          quote={selectedAcceptanceQuote}
          open={isAcceptanceDetailsOpen}
          onClose={() => {
            setIsAcceptanceDetailsOpen(false);
            setSelectedAcceptanceQuote(null);
          }}
        />
      )}
    </div>
  );
});

QuoteManager.displayName = 'QuoteManager';

export default QuoteManager;